import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { LOCALE_CODES, toBcp47 } from "@/i18n";
import { api, ApiError, type QueueRef } from "@/lib/api";
import {
  aiApi,
  type AiQueuePolicyCreate,
  type AiQueuePolicyOut,
  type AiQueuePolicyUpdate,
  type Autonomy,
  type IdentityMode,
  type ReplyLanguageMode,
} from "@/lib/aiApi";
import { AI_TASKS, missingNeeds, taskProfileMap } from "@/lib/aiTasks";
import {
  QueueTaskProfilesTable,
  type QueueTaskOverrides,
} from "@/components/admin/QueueTaskProfilesTable";
import {
  POLICIES_KEY,
  PROFILES_KEY,
  QUEUES_KEY,
  SETTINGS_KEY,
  TASK_DEFAULTS_KEY,
  invalidateCatalog,
} from "@/components/admin/ai-models/shared";
import { PickerField } from "@/components/admin/PickerField";
import { PromptPartsSection } from "@/components/admin/PromptPartsSection";
import { EscalationRuleTester } from "@/components/admin/EscalationRuleTester";
import { HelpPopover } from "@/components/ui/HelpPopover";
import { SelectField } from "@/components/ui/SelectField";
import { TagInput } from "@/components/ui/TagInput";
import { Tabs, type TabItem } from "@/components/ui/Tabs";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useConfirm } from "@/components/ui/ConfirmDialog";
import { cn } from "@/lib/cn";
import { TOKENS_PER_MIO } from "@/lib/aiLimits";

const NONE = 0;
const MAX_PROMPT_FILE_BYTES = 2 * 1024 * 1024;
const PROMPT_WARN_CHARS = 100_000;

const MCP_KEY = ["admin", "ai", "mcp-clients"] as const;
const AGENTS_KEY = ["admin", "ai", "reference-agents"] as const;

const AUTONOMY_VALUES: Autonomy[] = ["off", "clarify_only", "full"];
const IDENTITY_MODES: IdentityMode[] = [
  "ticket_customer_id",
  "clarify_schema",
  "off",
];
const REPLY_LANGUAGE_MODES: ReplyLanguageMode[] = ["off", "fixed", "auto"];
/** Znuny-compatible language codes for fixed/auto reply language. */
const REPLY_LANGUAGES = [...LOCALE_CODES];

type TabId =
  | "basics"
  | "drafts"
  | "summaries"
  | "auto"
  | "triage"
  | "safety";

type FormState = {
  queue_id: number;
  enabled_auto_reply: boolean;
  enabled_summary: boolean;
  enabled_manual_assist: boolean;
  enabled_refine: boolean;
  enabled_triage: boolean;
  routing_description: string;
  triage_target_queue_ids: Set<number>;
  triage_auto_threshold: string;
  triage_suggest_threshold: string;
  triage_samples: string;
  triage_customer_fix_enabled: boolean;
  triage_customer_fix_auto_threshold: string;
  triage_delay_reply: boolean;
  /** Overrides only — a task not in the map inherits the global default. */
  task_profiles: QueueTaskOverrides;
  autonomy: Autonomy;
  system_prompt: string;
  service_user_id: number;
  kb_tags: string;
  kb_category_ids: string;
  mcp_client_ids: Set<number>;
  summary_detail: "standard" | "detailed";
  summary_article_threshold: string;
  summary_char_threshold: string;
  summary_incremental_min_articles: string;
  summary_incremental_min_chars: string;
  max_clarifications: string;
  max_auto_replies: string;
  max_replies_per_hour: string;
  /** Daily token budget in MILLIONS of tokens ("3" = 3,000,000); the API
   * field `budget_tokens_day` stays in tokens. */
  budget_mio_tokens_day: string;
  escalation_rules: string;
  ai_disclosure_enabled: boolean;
  ai_disclosure_text: string;
  pii_masking: boolean;
  pii_ner_enabled: boolean;
  identity_mode: IdentityMode;
  clarify_schema_json: string;
  ignored_senders: string;
  ignore_senders_manual: boolean;
  reply_language_mode: ReplyLanguageMode;
  reply_language_fixed: string;
  reply_language_default: string;
  allowed_state_types: string;
};

/**
 * Defaults for a brand-new policy — chosen so a queue can go live without the
 * operator hand-tuning every cap. Never used on edit; existing policies
 * always load their real stored values via `toForm`.
 */
function emptyForm(queueId: number): FormState {
  return {
    queue_id: queueId,
    enabled_auto_reply: false,
    enabled_summary: false,
    enabled_manual_assist: false,
    enabled_refine: false,
    enabled_triage: false,
    routing_description: "",
    triage_target_queue_ids: new Set(),
    // 100 = "propose only". The thresholds are unmeasured on a fresh
    // install, so autonomy has to be a deliberate per-queue decision.
    triage_auto_threshold: "100",
    triage_suggest_threshold: "50",
    triage_samples: "3",
    triage_customer_fix_enabled: false,
    triage_customer_fix_auto_threshold: "100",
    triage_delay_reply: false,
    task_profiles: new Map(),
    autonomy: "off",
    system_prompt: "",
    service_user_id: NONE,
    kb_tags: "",
    kb_category_ids: "",
    mcp_client_ids: new Set(),
    summary_detail: "standard",
    summary_article_threshold: "10",
    summary_char_threshold: "20000",
    summary_incremental_min_articles: "2",
    summary_incremental_min_chars: "2000",
    max_clarifications: "2",
    max_auto_replies: "5",
    max_replies_per_hour: "20",
    budget_mio_tokens_day: "3",
    escalation_rules: "",
    ai_disclosure_enabled: false,
    ai_disclosure_text: "",
    pii_masking: true,
    pii_ner_enabled: true,
    identity_mode: "ticket_customer_id",
    clarify_schema_json: "",
    ignored_senders: "",
    ignore_senders_manual: false,
    reply_language_mode: "off",
    reply_language_fixed: "",
    reply_language_default: "",
    allowed_state_types: "",
  };
}

/** Newline/comma-tolerant textarea <-> stored-string conversion for
 * `ignored_senders` (one address/glob per line in the UI, comma-joined on
 * the wire — matches the backend's tolerant JSON-or-CSV parsing). */
function sendersRawToLines(raw: string | null): string {
  if (!raw || !raw.trim()) return "";
  const trimmed = raw.trim();
  try {
    const parsed: unknown = JSON.parse(trimmed);
    if (Array.isArray(parsed)) {
      return parsed
        .map((s) => String(s).trim())
        .filter(Boolean)
        .join("\n");
    }
  } catch {
    // not JSON — fall through to CSV/newline splitting
  }
  return trimmed
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
    .join("\n");
}

function linesToSendersRaw(text: string): string | null {
  const items = text
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);
  return items.length > 0 ? items.join(",") : null;
}

/**
 * Tolerant id-list parse for the Text columns that hold a JSON array but,
 * historically, sometimes a CSV instead (mirrors the backend's
 * `tiqora.ai.listfields.parse_int_list`).
 */
function parseIdList(raw: string | null): number[] {
  if (!raw || raw.trim() === "") return [];
  const text = raw.trim();
  try {
    const parsed: unknown = JSON.parse(text);
    if (Array.isArray(parsed)) {
      return parsed.map(Number).filter((n) => Number.isFinite(n) && n > 0);
    }
  } catch {
    // Fall through to the CSV shape.
  }
  return text
    .split(",")
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);
}

function toForm(row: AiQueuePolicyOut): FormState {
  return {
    queue_id: row.queue_id,
    enabled_auto_reply: row.enabled_auto_reply,
    enabled_summary: row.enabled_summary,
    enabled_manual_assist: row.enabled_manual_assist,
    enabled_refine: row.enabled_refine,
    enabled_triage: row.enabled_triage,
    routing_description: row.routing_description ?? "",
    triage_target_queue_ids: new Set(
      parseIdList(row.triage_target_queue_ids),
    ),
    triage_auto_threshold: String(row.triage_auto_threshold),
    triage_suggest_threshold: String(row.triage_suggest_threshold),
    triage_samples: String(row.triage_samples),
    triage_customer_fix_enabled: row.triage_customer_fix_enabled,
    triage_customer_fix_auto_threshold: String(
      row.triage_customer_fix_auto_threshold,
    ),
    triage_delay_reply: row.triage_delay_reply,
    task_profiles: taskProfileMap(row.task_profiles),
    autonomy: row.autonomy,
    system_prompt: row.system_prompt,
    service_user_id: row.service_user_id ?? NONE,
    kb_tags: row.kb_tags ?? "",
    kb_category_ids: row.kb_category_ids ?? "",
    mcp_client_ids: new Set(
      (row.mcp_client_ids ?? "")
        .split(",")
        .map((s) => s.trim())
        .filter((s) => s !== "")
        .map(Number)
        .filter((n) => Number.isFinite(n)),
    ),
    summary_detail: row.summary_detail,
    summary_article_threshold:
      row.summary_article_threshold != null
        ? String(row.summary_article_threshold)
        : "",
    summary_char_threshold:
      row.summary_char_threshold != null
        ? String(row.summary_char_threshold)
        : "",
    summary_incremental_min_articles:
      row.summary_incremental_min_articles != null
        ? String(row.summary_incremental_min_articles)
        : "",
    summary_incremental_min_chars:
      row.summary_incremental_min_chars != null
        ? String(row.summary_incremental_min_chars)
        : "",
    max_clarifications: String(row.max_clarifications),
    max_auto_replies: String(row.max_auto_replies),
    max_replies_per_hour:
      row.max_replies_per_hour != null ? String(row.max_replies_per_hour) : "",
    budget_mio_tokens_day:
      row.budget_tokens_day != null ? String(row.budget_tokens_day / TOKENS_PER_MIO) : "",
    escalation_rules: row.escalation_rules ?? "",
    ai_disclosure_enabled: row.ai_disclosure_enabled,
    ai_disclosure_text: row.ai_disclosure_text ?? "",
    pii_masking: row.pii_masking,
    pii_ner_enabled: row.pii_ner_enabled,
    identity_mode: row.identity_mode,
    clarify_schema_json: row.clarify_schema_json ?? "",
    ignored_senders: sendersRawToLines(row.ignored_senders),
    ignore_senders_manual: row.ignore_senders_manual,
    reply_language_mode: row.reply_language_mode,
    reply_language_fixed: row.reply_language_fixed ?? "",
    reply_language_default: row.reply_language_default ?? "",
    allowed_state_types: row.allowed_state_types ?? "",
  };
}

function numOrNull(v: string): number | null {
  const trimmed = v.trim();
  if (!trimmed) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/** "3" / "0,5" (millions) → whole tokens; empty → no budget. */
function mioToTokens(v: string): number | null {
  const n = numOrNull(v.replace(",", "."));
  return n == null ? null : Math.round(n * TOKENS_PER_MIO);
}

function validateJson(
  label: string,
  value: string,
  t: (k: string, o?: Record<string, unknown>) => string,
): string | null {
  if (!value.trim()) return null;
  try {
    JSON.parse(value);
    return null;
  } catch {
    return t("admin.ai.queues.invalidJson", { field: label });
  }
}

/** Field label + inline {@link HelpPopover} trigger — used on every editor field. */
function FieldLabel({
  text,
  help,
  defaultHint,
  testId,
}: {
  text: string;
  help: ReactNode;
  defaultHint?: string;
  testId: string;
}) {
  return (
    <span className="mb-1 flex items-center gap-1.5 text-muted">
      {text}
      <HelpPopover title={text} defaultHint={defaultHint} testId={testId}>
        {help}
      </HelpPopover>
    </span>
  );
}

/** Tab label with a green "feature enabled" dot, matching the artefact mock. */
function TabLabel({
  label,
  active,
  testId,
}: {
  label: string;
  active: boolean;
  testId?: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      {active && (
        <span
          className="h-1.5 w-1.5 rounded-full bg-green"
          data-testid={testId}
        />
      )}
    </span>
  );
}

const inputClass =
  "w-full rounded-md border border-hairline bg-surface-subtle px-3 py-1.5 text-sm text-ink disabled:cursor-not-allowed disabled:opacity-50";

function AiQueuePolicyEditor({ policyId }: { policyId?: number }) {
  const { t, i18n } = useTranslation();
  const locale = toBcp47(i18n.language);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { confirm, dialog: confirmDialog } = useConfirm();
  const isEdit = policyId != null;

  const [tab, setTab] = useState<TabId>("basics");
  const [form, setForm] = useState<FormState | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [jsonErrors, setJsonErrors] = useState<{
    escalation?: string;
    clarify?: string;
  }>({});
  const [promptFileError, setPromptFileError] = useState<string | null>(null);
  const autoProfileApplied = useRef(false);
  const promptFileInputRef = useRef<HTMLInputElement>(null);

  const policiesQ = useQuery({
    queryKey: POLICIES_KEY,
    queryFn: ({ signal }) => aiApi.listQueuePolicies(signal),
  });
  const queuesQ = useQuery({
    queryKey: QUEUES_KEY,
    queryFn: ({ signal }) => api.listReferenceQueues({}, signal),
  });
  const profilesQ = useQuery({
    queryKey: PROFILES_KEY,
    queryFn: ({ signal }) => aiApi.listProfiles(signal),
  });
  const taskDefaultsQ = useQuery({
    queryKey: TASK_DEFAULTS_KEY,
    queryFn: ({ signal }) => aiApi.getTaskDefaults(signal),
  });
  const globalDefaults = useMemo(
    () => taskProfileMap(taskDefaultsQ.data),
    [taskDefaultsQ.data],
  );
  const mcpQ = useQuery({
    queryKey: MCP_KEY,
    queryFn: ({ signal }) => aiApi.listMcpClients(signal),
  });
  const agentsQ = useQuery({
    queryKey: AGENTS_KEY,
    queryFn: ({ signal }) => api.listReferenceAgents(signal),
  });
  const settingsQ = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: ({ signal }) => aiApi.getSettings(signal),
  });
  const kbTagsQ = useQuery({
    queryKey: ["kb", "tags"],
    queryFn: ({ signal }) => api.listKbTags(signal),
  });
  const kbCategoriesQ = useQuery({
    queryKey: ["kb", "categories"],
    queryFn: ({ signal }) => api.listKbCategories(signal),
  });

  // kb_category_ids stays a CSV string on the wire (tolerant backend parsing);
  // the UI works on the parsed id set.
  const selectedKbCategoryIds = new Set(
    (form?.kb_category_ids ?? "")
      .split(",")
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0),
  );
  const toggleKbCategory = (id: number) => {
    const next = new Set(selectedKbCategoryIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setField(
      "kb_category_ids",
      Array.from(next)
        .sort((a, b) => a - b)
        .join(","),
    );
  };

  const editingRow = useMemo(
    () =>
      isEdit
        ? ((policiesQ.data?.items ?? []).find((p) => p.id === policyId) ?? null)
        : null,
    [isEdit, policiesQ.data, policyId],
  );

  const availableQueues: QueueRef[] = useMemo(() => {
    const used = new Set((policiesQ.data?.items ?? []).map((p) => p.queue_id));
    return (queuesQ.data ?? []).filter((q) => !used.has(q.id));
  }, [queuesQ.data, policiesQ.data]);

  /**
   * Selected target queues that have no routing_description of their own.
   *
   * This is the single likeliest misconfiguration: without a description the
   * model is offered nothing but the queue's name, which silently degrades
   * routing instead of failing. Worth a visible warning rather than a doc note.
   */
  const undescribedTargets = useMemo(() => {
    if (!form) return [];
    const described = new Set(
      (policiesQ.data?.items ?? [])
        .filter((row) => (row.routing_description ?? "").trim() !== "")
        .map((row) => row.queue_id),
    );
    return (queuesQ.data ?? [])
      .filter(
        (q) => form.triage_target_queue_ids.has(q.id) && !described.has(q.id),
      )
      .map((q) => q.name);
  }, [form, policiesQ.data, queuesQ.data]);

  // Create: seed the form once queues are known (first available queue).
  useEffect(() => {
    if (isEdit || form) return;
    if (!queuesQ.data) return;
    setForm(emptyForm(availableQueues[0]?.id ?? NONE));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, queuesQ.data]);

  // Edit: load the real stored values once the policy row is resolved.
  useEffect(() => {
    if (!isEdit || !editingRow) return;
    setForm(toForm(editingRow));
  }, [isEdit, editingRow]);

  // Create only, once: with exactly one profile and no global default for
  // "Recherche und Werkzeuge", preselect that profile for the task — the
  // successor of "auto-pick the sole provider". Never overrides a choice the
  // operator already made.
  useEffect(() => {
    if (isEdit || autoProfileApplied.current || !form) return;
    if (!profilesQ.data || !taskDefaultsQ.data) return;
    autoProfileApplied.current = true;
    const globalAgent = globalDefaults.get("agent");
    const globalAgentActive =
      globalAgent != null &&
      profilesQ.data.some((p) => p.id === globalAgent && p.valid_id === 1);
    // A global default pointing at a disabled profile counts as none.
    if (form.task_profiles.has("agent") || globalAgentActive) return;
    const only = profilesQ.data.length === 1 ? profilesQ.data[0] : null;
    if (!only || only.valid_id !== 1) return;
    if (missingNeeds("agent", only.entries).length > 0) return;
    setForm((f) =>
      f
        ? { ...f, task_profiles: new Map(f.task_profiles).set("agent", only.id) }
        : f,
    );
  }, [isEdit, form, profilesQ.data, taskDefaultsQ.data, globalDefaults]);

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  const toggleMcpClient = (id: number) => {
    setForm((f) => {
      if (!f) return f;
      const next = new Set(f.mcp_client_ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { ...f, mcp_client_ids: next };
    });
  };

  const applyPromptFileContent = (text: string) => {
    // A null byte (or a very high ratio of other non-printable control
    // characters) means the "text" file isn't actually text --
    // `FileReader.readAsText` would otherwise silently hand back mojibake
    // instead of failing.
    let controlChars = 0;
    for (let i = 0; i < text.length; i++) {
      const code = text.charCodeAt(i);
      if (code === 0 || (code < 32 && code !== 9 && code !== 10 && code !== 13))
        controlChars++;
    }
    if (controlChars / Math.max(text.length, 1) > 0.01) {
      setPromptFileError(t("admin.ai.queues.promptFileBinary"));
      return;
    }
    setPromptFileError(null);
    setField("system_prompt", text);
  };

  const readFileAsText = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () =>
        resolve(typeof reader.result === "string" ? reader.result : "");
      reader.onerror = () =>
        reject(reader.error ?? new Error("file read failed"));
      reader.readAsText(file);
    });

  const handlePromptFileSelected = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setPromptFileError(null);

    if (file.size > MAX_PROMPT_FILE_BYTES) {
      setPromptFileError(t("admin.ai.queues.promptFileTooLarge"));
      return;
    }

    let text: string;
    try {
      text = await readFileAsText(file);
    } catch {
      setPromptFileError(t("admin.ai.queues.promptFileBinary"));
      return;
    }
    if (form && form.system_prompt.trim()) {
      const ok = await confirm({
        title: t("admin.ai.queues.promptReplaceConfirmTitle"),
        message: t("admin.ai.queues.promptReplaceConfirmMessage"),
      });
      if (!ok) return;
    }
    applyPromptFileContent(text);
  };

  // Profiles list their queue uses (`used_by`), so refresh the catalog too.
  const invalidate = () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: POLICIES_KEY }),
      invalidateCatalog(qc),
    ]);

  const createM = useMutation({
    mutationFn: (body: AiQueuePolicyCreate) => aiApi.createQueuePolicy(body),
    onSuccess: async () => {
      await invalidate();
      await navigate({ to: "/admin/ai/queues" });
    },
  });
  const updateM = useMutation({
    mutationFn: ({ id, body }: { id: number; body: AiQueuePolicyUpdate }) =>
      aiApi.updateQueuePolicy(id, body),
    onSuccess: async () => {
      await invalidate();
      await navigate({ to: "/admin/ai/queues" });
    },
  });

  const buildBody = (f: FormState): Omit<AiQueuePolicyCreate, "queue_id"> => ({
    enabled_auto_reply: f.enabled_auto_reply,
    enabled_summary: f.enabled_summary,
    enabled_manual_assist: f.enabled_manual_assist,
    enabled_refine: f.enabled_refine,
    enabled_triage: f.enabled_triage,
    routing_description: f.routing_description.trim() || null,
    triage_target_queue_ids:
      f.triage_target_queue_ids.size > 0
        ? JSON.stringify(Array.from(f.triage_target_queue_ids))
        : null,
    triage_auto_threshold: numOrNull(f.triage_auto_threshold) ?? 100,
    triage_suggest_threshold: numOrNull(f.triage_suggest_threshold) ?? 50,
    triage_samples: numOrNull(f.triage_samples) ?? 3,
    triage_customer_fix_enabled: f.triage_customer_fix_enabled,
    triage_customer_fix_auto_threshold:
      numOrNull(f.triage_customer_fix_auto_threshold) ?? 100,
    triage_delay_reply: f.triage_delay_reply,
    // The whole override set (PUT replaces it); inherited tasks are omitted.
    task_profiles: AI_TASKS.filter((task) => f.task_profiles.has(task)).map(
      (task) => ({ task, profile_id: f.task_profiles.get(task) ?? null }),
    ),
    autonomy: f.autonomy,
    system_prompt: f.system_prompt,
    service_user_id: f.service_user_id !== NONE ? f.service_user_id : null,
    kb_tags: f.kb_tags.trim() || null,
    kb_category_ids: f.kb_category_ids.trim() || null,
    mcp_client_ids:
      f.mcp_client_ids.size > 0 ? Array.from(f.mcp_client_ids).join(",") : null,
    summary_detail: f.summary_detail,
    summary_article_threshold: numOrNull(f.summary_article_threshold),
    summary_char_threshold: numOrNull(f.summary_char_threshold),
    summary_incremental_min_articles: numOrNull(
      f.summary_incremental_min_articles,
    ),
    summary_incremental_min_chars: numOrNull(f.summary_incremental_min_chars),
    max_clarifications: numOrNull(f.max_clarifications) ?? 2,
    max_auto_replies: numOrNull(f.max_auto_replies) ?? 5,
    max_replies_per_hour: numOrNull(f.max_replies_per_hour),
    budget_tokens_day: mioToTokens(f.budget_mio_tokens_day),
    escalation_rules: f.escalation_rules.trim() || null,
    ai_disclosure_enabled: f.ai_disclosure_enabled,
    ai_disclosure_text: f.ai_disclosure_text.trim() || null,
    pii_masking: f.pii_masking,
    pii_ner_enabled: f.pii_ner_enabled,
    identity_mode: f.identity_mode,
    clarify_schema_json: f.clarify_schema_json.trim() || null,
    ignored_senders: linesToSendersRaw(f.ignored_senders),
    ignore_senders_manual: f.ignore_senders_manual,
    reply_language_mode: f.reply_language_mode,
    reply_language_fixed: f.reply_language_fixed.trim() || null,
    reply_language_default: f.reply_language_default.trim() || null,
    allowed_state_types: f.allowed_state_types.trim() || null,
  });

  const handleSave = async () => {
    if (!form) return;
    setFormError(null);
    const escalationErr = validateJson(
      "escalation_rules",
      form.escalation_rules,
      t,
    );
    const clarifyErr = validateJson(
      "clarify_schema_json",
      form.clarify_schema_json,
      t,
    );
    setJsonErrors({
      escalation: escalationErr ?? undefined,
      clarify: clarifyErr ?? undefined,
    });
    if (escalationErr || clarifyErr) return;

    try {
      if (isEdit) {
        await updateM.mutateAsync({ id: policyId, body: buildBody(form) });
      } else {
        if (form.queue_id === NONE) {
          setFormError(t("admin.ai.queues.queueRequired"));
          return;
        }
        await createM.mutateAsync({
          queue_id: form.queue_id,
          ...buildBody(form),
        });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setFormError(t("admin.ai.queues.gateError"));
      } else {
        setFormError(
          err instanceof ApiError ? err.message : t("admin.form.genericError"),
        );
      }
    }
  };

  const isLoading = isEdit
    ? policiesQ.isLoading || (policiesQ.isSuccess && !editingRow)
    : !form;

  if (isEdit && policiesQ.isSuccess && !editingRow) {
    return (
      <div
        className="p-4 text-sm text-danger"
        data-testid="admin-ai-queue-editor-page"
      >
        {t("admin.ai.queues.editor.loadError")}
      </div>
    );
  }

  if (isLoading || !form) {
    return (
      <div
        className="flex items-center gap-2 p-4"
        data-testid="admin-ai-queue-editor-page"
      >
        <Spinner />
      </div>
    );
  }

  const gateOpen = settingsQ.data?.operation_mode === "tiqora_primary";
  const queueName =
    (queuesQ.data ?? []).find((q) => q.id === form.queue_id)?.name ??
    `#${form.queue_id}`;

  const tabs: TabItem[] = [
    { id: "basics", label: t("admin.ai.queues.tab.basics") },
    {
      id: "drafts",
      label: (
        <TabLabel
          label={t("admin.ai.queues.tab.drafts")}
          active={form.enabled_manual_assist}
          testId="admin-ai-queue-tab-dot-drafts"
        />
      ),
    },
    {
      id: "summaries",
      label: (
        <TabLabel
          label={t("admin.ai.queues.tab.summaries")}
          active={form.enabled_summary}
          testId="admin-ai-queue-tab-dot-summaries"
        />
      ),
    },
    {
      id: "auto",
      label: (
        <TabLabel
          label={t("admin.ai.queues.tab.auto")}
          active={form.enabled_auto_reply}
          testId="admin-ai-queue-tab-dot-auto"
        />
      ),
    },
    {
      id: "triage",
      label: (
        <TabLabel
          label={t("admin.ai.queues.tab.triage")}
          active={form.enabled_triage}
          testId="admin-ai-queue-tab-dot-triage"
        />
      ),
    },
    { id: "safety", label: t("admin.ai.queues.tab.safety") },
  ];

  return (
    <div
      className="mx-auto w-full max-w-4xl space-y-4 p-4"
      data-testid="admin-ai-queue-editor-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold text-ink">
            {isEdit ? queueName : t("admin.ai.queues.editor.newTitle")}
          </h1>
          <p className="text-xs text-muted">
            {t("admin.ai.queues.description")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/ai/queues"
            className="text-sm text-muted hover:text-ink hover:underline"
            data-testid="admin-ai-queue-editor-cancel"
          >
            {t("admin.form.cancel")}
          </Link>
          <Button
            type="button"
            variant="primary"
            data-testid="admin-ai-queue-editor-save"
            disabled={createM.isPending || updateM.isPending}
            onClick={() => void handleSave()}
          >
            {createM.isPending || updateM.isPending
              ? t("admin.form.saving")
              : t("admin.form.save")}
          </Button>
        </div>
      </div>

      <Tabs items={tabs} value={tab} onChange={(id) => setTab(id as TabId)} />

      {formError && (
        <p
          className="text-sm text-escalation"
          data-testid="admin-ai-queue-editor-error"
        >
          {formError}
        </p>
      )}

      <div className="rounded-lg border border-hairline bg-surface p-4">
        {tab === "basics" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm sm:col-span-2">
              <FieldLabel
                text={t("admin.ai.queues.queue")}
                help={t("admin.help.aiQueue.queue")}
                testId="admin-ai-queue-help-queue"
              />
              {isEdit ? (
                <>
                  <p className="rounded-md border border-hairline bg-surface-subtle px-3 py-1.5 text-sm text-muted">
                    {queueName}
                  </p>
                  <p className="mt-1 text-xs text-muted">
                    {t("admin.ai.queues.editor.queueLocked")}
                  </p>
                </>
              ) : (
                <PickerField
                  testId="admin-ai-queue-form-queue_id"
                  value={form.queue_id}
                  items={availableQueues.map((q) => ({
                    value: q.id,
                    label: q.name,
                  }))}
                  placeholder={t("admin.form.selectPlaceholder")}
                  onSelect={(v) => setField("queue_id", v)}
                />
              )}
            </label>

            <div
              className="rounded-lg border border-hairline p-3 sm:col-span-2"
              data-testid="admin-ai-queue-language-section"
            >
              <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted">
                {t("admin.ai.queues.section.language")}
              </h3>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.replyLanguageMode.label")}
                    help={t("admin.help.aiQueue.replyLanguageMode")}
                    defaultHint={t("admin.ai.queues.replyLanguageMode.off")}
                    testId="admin-ai-queue-help-reply_language_mode"
                  />
                  <PickerField
                    testId="admin-ai-queue-form-reply_language_mode"
                    value={form.reply_language_mode}
                    items={REPLY_LANGUAGE_MODES.map((m) => ({
                      value: m,
                      label: t(`admin.ai.queues.replyLanguageMode.${m}`),
                    }))}
                    placeholder={t("admin.form.selectPlaceholder")}
                    onSelect={(v) => {
                      setField("reply_language_mode", v);
                      // `fixed` requires a language — preselect German instead
                      // of surfacing a 422 for a field that looks pre-filled.
                      if (
                        v === "fixed" &&
                        !REPLY_LANGUAGES.includes(form.reply_language_fixed)
                      ) {
                        setField("reply_language_fixed", "de");
                      }
                    }}
                  />
                </label>
                {form.reply_language_mode === "fixed" && (
                  <label className="block text-sm">
                    <FieldLabel
                      text={t("admin.ai.queues.replyLanguageFixed")}
                      help={t("admin.help.aiQueue.replyLanguageFixed")}
                      testId="admin-ai-queue-help-reply_language_fixed"
                    />
                    <SelectField
                      items={REPLY_LANGUAGES.map((l) => ({
                        value: l,
                        label: t(`admin.ai.queues.replyLanguage.${l}`),
                      }))}
                      value={
                        REPLY_LANGUAGES.includes(form.reply_language_fixed)
                          ? form.reply_language_fixed
                          : "de"
                      }
                      onChange={(v) => setField("reply_language_fixed", v)}
                      testId="admin-ai-queue-form-reply_language_fixed"
                    />
                  </label>
                )}
                {form.reply_language_mode === "auto" && (
                  <label className="block text-sm">
                    <FieldLabel
                      text={t("admin.ai.queues.replyLanguageDefault")}
                      help={t("admin.help.aiQueue.replyLanguageDefault")}
                      defaultHint={t("admin.ai.queues.replyLanguageNone")}
                      testId="admin-ai-queue-help-reply_language_default"
                    />
                    <SelectField
                      items={[
                        {
                          value: "",
                          label: t("admin.ai.queues.replyLanguageNone"),
                        },
                        ...REPLY_LANGUAGES.map((l) => ({
                          value: l,
                          label: t(`admin.ai.queues.replyLanguage.${l}`),
                        })),
                      ]}
                      value={
                        REPLY_LANGUAGES.includes(form.reply_language_default)
                          ? form.reply_language_default
                          : ""
                      }
                      onChange={(v) => setField("reply_language_default", v)}
                      testId="admin-ai-queue-form-reply_language_default"
                    />
                  </label>
                )}
              </div>
            </div>

            <div className="block text-sm sm:col-span-2">
              <div className="mb-1 flex items-center justify-between gap-2">
                <FieldLabel
                  text={t("admin.ai.queues.section.prompt")}
                  help={t("admin.help.aiQueue.systemPrompt")}
                  testId="admin-ai-queue-help-system_prompt"
                />
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  data-testid="admin-ai-queue-form-prompt-load-file"
                  onClick={() => promptFileInputRef.current?.click()}
                >
                  {t("admin.ai.queues.promptLoadFile")}
                </Button>
                <input
                  ref={promptFileInputRef}
                  type="file"
                  accept=".txt,.md,text/plain,text/markdown"
                  data-testid="admin-ai-queue-form-prompt-file-input"
                  className="hidden"
                  onChange={(e) => void handlePromptFileSelected(e)}
                />
              </div>
              <textarea
                data-testid="admin-ai-queue-form-system_prompt"
                value={form.system_prompt}
                onChange={(e) => setField("system_prompt", e.target.value)}
                rows={14}
                className={cn(inputClass, "font-mono text-xs")}
              />
              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                <span
                  className="text-muted"
                  data-testid="admin-ai-queue-form-prompt-char-count"
                >
                  {t("admin.ai.queues.promptCharCount", {
                    formatted: new Intl.NumberFormat(locale).format(
                      form.system_prompt.length,
                    ),
                  })}
                </span>
                {form.system_prompt.length >= PROMPT_WARN_CHARS && (
                  <span
                    className="text-amber"
                    data-testid="admin-ai-queue-form-prompt-char-warning"
                  >
                    {t("admin.ai.queues.promptCharCountWarning")}
                  </span>
                )}
              </div>
              {promptFileError && (
                <p
                  className="mt-1 text-xs text-escalation"
                  data-testid="admin-ai-queue-form-prompt-file-error"
                >
                  {promptFileError}
                </p>
              )}
            </div>

            {isEdit && policyId != null && (
              <PromptPartsSection policyId={policyId} />
            )}

            <div className="sm:col-span-2">
              {profilesQ.data && taskDefaultsQ.data ? (
                <QueueTaskProfilesTable
                  value={form.task_profiles}
                  onChange={(next) => setField("task_profiles", next)}
                  profiles={profilesQ.data}
                  globalDefaults={globalDefaults}
                />
              ) : (
                <div
                  className="flex justify-center py-6"
                  data-testid="admin-ai-queue-models-loading"
                >
                  <Spinner />
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "drafts" && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-enabled_manual_assist"
                checked={form.enabled_manual_assist}
                onChange={(e) =>
                  setField("enabled_manual_assist", e.target.checked)
                }
                className="rounded border-hairline"
              />
              {t("admin.ai.feature.manual_assist")}
              <HelpPopover
                title={t("admin.ai.feature.manual_assist")}
                testId="admin-ai-queue-help-enabled_manual_assist"
              >
                {t("admin.help.aiQueue.enabledManualAssist")}
              </HelpPopover>
            </label>

            {/* Independent of manual assist: refine rewrites what the agent
                already typed, it never drafts a reply. */}
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-enabled_refine"
                checked={form.enabled_refine}
                onChange={(e) => setField("enabled_refine", e.target.checked)}
                className="rounded border-hairline"
              />
              {t("admin.ai.feature.refine")}
              <HelpPopover
                title={t("admin.ai.feature.refine")}
                testId="admin-ai-queue-help-enabled_refine"
              >
                {t("admin.help.aiQueue.enabledRefine")}
              </HelpPopover>
            </label>

            <fieldset
              disabled={!form.enabled_manual_assist}
              className={cn(
                "grid gap-4 sm:grid-cols-2",
                !form.enabled_manual_assist && "opacity-50",
              )}
            >
              <label className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.kbTags")}
                  help={t("admin.help.aiQueue.kbTags")}
                  testId="admin-ai-queue-help-kb_tags"
                />
                <TagInput
                  testId="admin-ai-queue-form-kb_tags"
                  value={form.kb_tags
                    .split(",")
                    .map((s) => s.trim())
                    .filter(Boolean)}
                  onChange={(tags) => setField("kb_tags", tags.join(","))}
                  suggestions={(kbTagsQ.data ?? []).map((tg) => ({
                    name: tg.name,
                    count: tg.article_count,
                  }))}
                  placeholder={t("admin.ai.queues.kbTagsPlaceholder")}
                />
              </label>
              <div className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.kbCategories")}
                  help={t("admin.help.aiQueue.kbCategoryIds")}
                  testId="admin-ai-queue-help-kb_category_ids"
                />
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="admin-ai-queue-form-kb_category_ids"
                >
                  {(kbCategoriesQ.data ?? []).map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-1.5 rounded-md border border-hairline bg-surface-subtle px-2 py-1.5 text-xs text-ink"
                    >
                      <input
                        type="checkbox"
                        data-testid={`admin-ai-queue-form-kb-category-${c.id}`}
                        checked={selectedKbCategoryIds.has(c.id)}
                        onChange={() => toggleKbCategory(c.id)}
                        className="rounded border-hairline"
                      />
                      {c.name}
                    </label>
                  ))}
                  {(kbCategoriesQ.data?.length ?? 0) === 0 && (
                    <span className="text-xs text-muted">
                      {t("admin.ai.queues.kbCategoriesEmpty")}
                    </span>
                  )}
                </div>
              </div>
              <div className="sm:col-span-2">
                <div className="mb-1 flex items-center gap-1.5">
                  <FieldLabel
                    text={t("admin.ai.queues.mcpClients")}
                    help={t("admin.help.aiQueue.mcpClients")}
                    testId="admin-ai-queue-help-mcp_clients"
                  />
                  <span
                    className="text-xs text-muted"
                    data-testid="admin-ai-queue-form-mcp-selected-count"
                  >
                    {t("admin.ai.queues.mcpClientsSelected", {
                      count: form.mcp_client_ids.size,
                    })}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(mcpQ.data?.items ?? []).map((c) => (
                    <label
                      key={c.id}
                      className="flex items-start gap-1.5 rounded-md border border-hairline bg-surface-subtle px-2 py-1.5 text-xs text-ink"
                    >
                      <input
                        type="checkbox"
                        data-testid={`admin-ai-queue-form-mcp-${c.id}`}
                        checked={form.mcp_client_ids.has(c.id)}
                        onChange={() => toggleMcpClient(c.id)}
                        className="mt-0.5 rounded border-hairline"
                      />
                      <span>
                        <span className="block">{c.name}</span>
                        <span className="block text-[11px] text-muted">
                          {c.url}
                        </span>
                      </span>
                    </label>
                  ))}
                  {(mcpQ.data?.items.length ?? 0) === 0 && (
                    <span className="text-xs text-muted">
                      {t("admin.ai.mcp.empty")}
                    </span>
                  )}
                </div>
              </div>
            </fieldset>
          </div>
        )}

        {tab === "summaries" && (
          <div className="space-y-4">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-enabled_summary"
                checked={form.enabled_summary}
                onChange={(e) => setField("enabled_summary", e.target.checked)}
                className="rounded border-hairline"
              />
              {t("admin.ai.feature.summary")}
              <HelpPopover
                title={t("admin.ai.feature.summary")}
                testId="admin-ai-queue-help-enabled_summary"
              >
                {t("admin.help.aiQueue.enabledSummary")}
              </HelpPopover>
            </label>

            <fieldset
              disabled={!form.enabled_summary}
              className={cn(
                "grid gap-4 sm:grid-cols-2",
                !form.enabled_summary && "opacity-50",
              )}
            >
              <label className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.summaryArticleThreshold")}
                  help={t("admin.help.aiQueue.summaryArticleThreshold")}
                  defaultHint={t("admin.ai.queues.emptyUnlimited")}
                  testId="admin-ai-queue-help-summary_article_threshold"
                />
                <input
                  type="number"
                  data-testid="admin-ai-queue-form-summary_article_threshold"
                  value={form.summary_article_threshold}
                  onChange={(e) =>
                    setField("summary_article_threshold", e.target.value)
                  }
                  placeholder={t("admin.ai.queues.emptyUnlimited")}
                  className={inputClass}
                />
              </label>
              <label className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.summaryCharThreshold")}
                  help={t("admin.help.aiQueue.summaryCharThreshold")}
                  defaultHint={t("admin.ai.queues.emptyUnlimited")}
                  testId="admin-ai-queue-help-summary_char_threshold"
                />
                <input
                  type="number"
                  data-testid="admin-ai-queue-form-summary_char_threshold"
                  value={form.summary_char_threshold}
                  onChange={(e) =>
                    setField("summary_char_threshold", e.target.value)
                  }
                  placeholder={t("admin.ai.queues.emptyUnlimited")}
                  className={inputClass}
                />
              </label>
              <label className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.summaryIncrementalMinArticles")}
                  help={t("admin.help.aiQueue.summaryIncrementalMinArticles")}
                  defaultHint={t("admin.ai.queues.emptyUnlimited")}
                  testId="admin-ai-queue-help-summary_incremental_min_articles"
                />
                <input
                  type="number"
                  data-testid="admin-ai-queue-form-summary_incremental_min_articles"
                  value={form.summary_incremental_min_articles}
                  onChange={(e) =>
                    setField("summary_incremental_min_articles", e.target.value)
                  }
                  placeholder={t("admin.ai.queues.emptyUnlimited")}
                  className={inputClass}
                />
              </label>
              <label className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.summaryIncrementalMinChars")}
                  help={t("admin.help.aiQueue.summaryIncrementalMinChars")}
                  defaultHint={t("admin.ai.queues.emptyUnlimited")}
                  testId="admin-ai-queue-help-summary_incremental_min_chars"
                />
                <input
                  type="number"
                  data-testid="admin-ai-queue-form-summary_incremental_min_chars"
                  value={form.summary_incremental_min_chars}
                  onChange={(e) =>
                    setField("summary_incremental_min_chars", e.target.value)
                  }
                  placeholder={t("admin.ai.queues.emptyUnlimited")}
                  className={inputClass}
                />
              </label>
            </fieldset>
          </div>
        )}

        {tab === "auto" && (
          <div className="space-y-4">
            <div className="space-y-2 border-b border-hairline pb-4">
              <FieldLabel
                text={t("admin.ai.queues.ignoredSenders")}
                help={t("admin.help.aiQueue.ignoredSenders")}
                testId="admin-ai-queue-help-ignored_senders"
              />
              <textarea
                data-testid="admin-ai-queue-form-ignored_senders"
                value={form.ignored_senders}
                onChange={(e) => setField("ignored_senders", e.target.value)}
                placeholder={t("admin.ai.queues.ignoredSendersPlaceholder")}
                rows={3}
                spellCheck={false}
                className={cn(inputClass, "font-mono text-xs")}
              />
              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  data-testid="admin-ai-queue-form-ignore_senders_manual"
                  checked={form.ignore_senders_manual}
                  onChange={(e) =>
                    setField("ignore_senders_manual", e.target.checked)
                  }
                  className="rounded border-hairline"
                />
                {t("admin.ai.queues.ignoreSendersManual")}
                <HelpPopover
                  title={t("admin.ai.queues.ignoreSendersManual")}
                  testId="admin-ai-queue-help-ignore_senders_manual"
                >
                  {t("admin.help.aiQueue.ignoreSendersManual")}
                </HelpPopover>
              </label>
            </div>

            {!gateOpen && (
              <p
                className="rounded-md border border-escalation/40 bg-escalation/10 p-2 text-xs text-escalation"
                data-testid="admin-ai-queue-auto-gate-warning"
              >
                {t("admin.ai.queues.autoGateWarning")}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-enabled_auto_reply"
                checked={form.enabled_auto_reply}
                disabled={!gateOpen && !form.enabled_auto_reply}
                onChange={(e) =>
                  setField("enabled_auto_reply", e.target.checked)
                }
                className="rounded border-hairline disabled:cursor-not-allowed disabled:opacity-50"
              />
              {t("admin.ai.feature.auto_reply")}
              <HelpPopover
                title={t("admin.ai.feature.auto_reply")}
                testId="admin-ai-queue-help-enabled_auto_reply"
              >
                {t("admin.help.aiQueue.enabledAutoReply")}
              </HelpPopover>
            </label>

            <fieldset
              disabled={!form.enabled_auto_reply}
              className={cn(
                "space-y-4",
                !form.enabled_auto_reply && "opacity-50",
              )}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.autonomy.label")}
                    help={t("admin.help.aiQueue.autonomy")}
                    defaultHint={t("admin.ai.queues.autonomy.off")}
                    testId="admin-ai-queue-help-autonomy"
                  />
                  <PickerField
                    testId="admin-ai-queue-form-autonomy"
                    value={form.autonomy}
                    items={AUTONOMY_VALUES.map((a) => ({
                      value: a,
                      label: t(`admin.ai.queues.autonomy.${a}`),
                    }))}
                    placeholder={t("admin.form.selectPlaceholder")}
                    onSelect={(v) => setField("autonomy", v)}
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.serviceUser")}
                    help={t("admin.help.aiQueue.serviceUser")}
                    testId="admin-ai-queue-help-service_user_id"
                  />
                  <PickerField
                    testId="admin-ai-queue-form-service_user_id"
                    value={form.service_user_id}
                    items={[
                      { value: NONE, label: t("admin.form.selectPlaceholder") },
                      ...(agentsQ.data ?? []).map((a) => ({
                        value: a.id,
                        label: a.full_name,
                        hint: a.login,
                      })),
                    ]}
                    placeholder={t("admin.form.selectPlaceholder")}
                    loading={agentsQ.isLoading}
                    onSelect={(v) => setField("service_user_id", v)}
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.maxClarifications")}
                    help={t("admin.help.aiQueue.maxClarifications")}
                    defaultHint="2"
                    testId="admin-ai-queue-help-max_clarifications"
                  />
                  <input
                    type="number"
                    data-testid="admin-ai-queue-form-max_clarifications"
                    value={form.max_clarifications}
                    onChange={(e) =>
                      setField("max_clarifications", e.target.value)
                    }
                    className={inputClass}
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.maxAutoReplies")}
                    help={t("admin.help.aiQueue.maxAutoReplies")}
                    defaultHint="5"
                    testId="admin-ai-queue-help-max_auto_replies"
                  />
                  <input
                    type="number"
                    data-testid="admin-ai-queue-form-max_auto_replies"
                    value={form.max_auto_replies}
                    onChange={(e) =>
                      setField("max_auto_replies", e.target.value)
                    }
                    className={inputClass}
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.maxRepliesPerHour")}
                    help={t("admin.help.aiQueue.maxRepliesPerHour")}
                    defaultHint={t("admin.ai.queues.emptyUnlimited")}
                    testId="admin-ai-queue-help-max_replies_per_hour"
                  />
                  <input
                    type="number"
                    data-testid="admin-ai-queue-form-max_replies_per_hour"
                    value={form.max_replies_per_hour}
                    onChange={(e) =>
                      setField("max_replies_per_hour", e.target.value)
                    }
                    placeholder={t("admin.ai.queues.emptyUnlimited")}
                    className={inputClass}
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.budgetTokensDay")}
                    help={t("admin.help.aiQueue.budgetTokensDay")}
                    defaultHint={t("admin.ai.queues.emptyUnlimited")}
                    testId="admin-ai-queue-help-budget_tokens_day"
                  />
                  <input
                    type="number"
                    min={0}
                    step={0.1}
                    data-testid="admin-ai-queue-form-budget_tokens_day"
                    value={form.budget_mio_tokens_day}
                    onChange={(e) =>
                      setField("budget_mio_tokens_day", e.target.value)
                    }
                    placeholder={t("admin.ai.queues.emptyUnlimited")}
                    className={inputClass}
                  />
                </label>
              </div>

              <div className="space-y-2 border-t border-hairline pt-3">
                <label className="flex items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    data-testid="admin-ai-queue-form-ai_disclosure_enabled"
                    checked={form.ai_disclosure_enabled}
                    onChange={(e) =>
                      setField("ai_disclosure_enabled", e.target.checked)
                    }
                    className="rounded border-hairline"
                  />
                  {t("admin.ai.queues.disclosureEnabled")}
                  <HelpPopover
                    title={t("admin.ai.queues.disclosureEnabled")}
                    testId="admin-ai-queue-help-ai_disclosure_enabled"
                  >
                    {t("admin.help.aiQueue.disclosureEnabled")}
                  </HelpPopover>
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.disclosureTextPlaceholder")}
                    help={t("admin.help.aiQueue.disclosureText")}
                    testId="admin-ai-queue-help-ai_disclosure_text"
                  />
                  <textarea
                    data-testid="admin-ai-queue-form-ai_disclosure_text"
                    value={form.ai_disclosure_text}
                    onChange={(e) =>
                      setField("ai_disclosure_text", e.target.value)
                    }
                    placeholder={t("admin.ai.queues.disclosureTextPlaceholder")}
                    rows={2}
                    className={inputClass}
                  />
                </label>
              </div>

              <div className="space-y-1 border-t border-hairline pt-3">
                <FieldLabel
                  text={t("admin.ai.queues.escalationRules")}
                  help={t("admin.help.aiQueue.escalationRules")}
                  testId="admin-ai-queue-help-escalation_rules"
                />
                <textarea
                  data-testid="admin-ai-queue-form-escalation_rules"
                  value={form.escalation_rules}
                  onChange={(e) => setField("escalation_rules", e.target.value)}
                  placeholder={t("admin.ai.queues.escalationRulesPlaceholder")}
                  rows={4}
                  spellCheck={false}
                  className={cn(inputClass, "font-mono text-xs")}
                />
                {jsonErrors.escalation && (
                  <p
                    className="text-xs text-escalation"
                    data-testid="admin-ai-queue-form-escalation_rules-error"
                  >
                    {jsonErrors.escalation}
                  </p>
                )}
                <EscalationRuleTester rulesJson={form.escalation_rules} />
              </div>
            </fieldset>
          </div>
        )}

        {tab === "triage" && (
          <div className="space-y-4">
            {!gateOpen && (
              <p
                className="rounded-md border border-escalation/40 bg-escalation/10 p-2 text-xs text-escalation"
                data-testid="admin-ai-queue-triage-gate-warning"
              >
                {t("admin.ai.queues.triageGateWarning")}
              </p>
            )}
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-enabled_triage"
                checked={form.enabled_triage}
                disabled={!gateOpen && !form.enabled_triage}
                onChange={(e) => setField("enabled_triage", e.target.checked)}
                className="rounded border-hairline disabled:cursor-not-allowed disabled:opacity-50"
              />
              {t("admin.ai.feature.triage")}
              <HelpPopover
                title={t("admin.ai.feature.triage")}
                testId="admin-ai-queue-help-enabled_triage"
              >
                {t("admin.help.aiQueue.enabledTriage")}
              </HelpPopover>
            </label>

            {/* Deliberately OUTSIDE the enabled_triage fieldset: this text
                describes THIS queue for OTHER queues' triage runs, so it is
                worth filling in even for a queue that never triages itself. */}
            <label className="block text-sm">
              <FieldLabel
                text={t("admin.ai.queues.routingDescription")}
                help={t("admin.help.aiQueue.routingDescription")}
                testId="admin-ai-queue-help-routing_description"
              />
              <textarea
                data-testid="admin-ai-queue-form-routing_description"
                value={form.routing_description}
                onChange={(e) =>
                  setField("routing_description", e.target.value)
                }
                rows={4}
                placeholder={t("admin.ai.queues.routingDescriptionPlaceholder")}
                className="mt-1 w-full rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink"
              />
              <p className="mt-1 text-xs text-muted">
                {t("admin.ai.queues.routingDescriptionHint")}
              </p>
            </label>

            <fieldset
              disabled={!form.enabled_triage}
              className={cn(
                "space-y-4",
                !form.enabled_triage && "opacity-50",
              )}
            >
              <div className="block text-sm">
                <FieldLabel
                  text={t("admin.ai.queues.triageTargets")}
                  help={t("admin.help.aiQueue.triageTargets")}
                  testId="admin-ai-queue-help-triage_target_queue_ids"
                />
                <div
                  className="flex flex-wrap gap-2"
                  data-testid="admin-ai-queue-form-triage_target_queue_ids"
                >
                  {(queuesQ.data ?? [])
                    .filter((q) => q.id !== form.queue_id)
                    .map((q) => (
                      <label
                        key={q.id}
                        className="flex items-center gap-1.5 rounded-md border border-hairline bg-surface-subtle px-2 py-1.5 text-xs text-ink"
                      >
                        <input
                          type="checkbox"
                          data-testid={`admin-ai-queue-form-triage-target-${q.id}`}
                          checked={form.triage_target_queue_ids.has(q.id)}
                          onChange={(e) =>
                            setForm((f) => {
                              if (!f) return f;
                              const next = new Set(f.triage_target_queue_ids);
                              if (e.target.checked) next.add(q.id);
                              else next.delete(q.id);
                              return { ...f, triage_target_queue_ids: next };
                            })
                          }
                          className="rounded border-hairline"
                        />
                        {q.name}
                      </label>
                    ))}
                </div>
                {undescribedTargets.length > 0 && (
                  <p
                    className="mt-2 rounded-md border border-amber/30 bg-amber/15 p-2 text-xs text-ink"
                    data-testid="admin-ai-queue-triage-missing-description"
                  >
                    {t("admin.ai.queues.triageTargetsNoDescription", {
                      queues: undescribedTargets.join(", "),
                    })}
                  </p>
                )}
              </div>

              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.triageAutoThreshold")}
                    help={t("admin.help.aiQueue.triageAutoThreshold")}
                    testId="admin-ai-queue-help-triage_auto_threshold"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    data-testid="admin-ai-queue-form-triage_auto_threshold"
                    value={form.triage_auto_threshold}
                    onChange={(e) =>
                      setField("triage_auto_threshold", e.target.value)
                    }
                    className="mt-1 w-full rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink"
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.triageSuggestThreshold")}
                    help={t("admin.help.aiQueue.triageSuggestThreshold")}
                    testId="admin-ai-queue-help-triage_suggest_threshold"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    data-testid="admin-ai-queue-form-triage_suggest_threshold"
                    value={form.triage_suggest_threshold}
                    onChange={(e) =>
                      setField("triage_suggest_threshold", e.target.value)
                    }
                    className="mt-1 w-full rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink"
                  />
                </label>
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.triageSamples")}
                    help={t("admin.help.aiQueue.triageSamples")}
                    testId="admin-ai-queue-help-triage_samples"
                  />
                  <input
                    type="number"
                    min={1}
                    max={5}
                    data-testid="admin-ai-queue-form-triage_samples"
                    value={form.triage_samples}
                    onChange={(e) =>
                      setField("triage_samples", e.target.value)
                    }
                    className="mt-1 w-full rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink"
                  />
                </label>
              </div>

              <p
                className="rounded-md border border-hairline bg-surface-subtle p-2 text-xs text-muted"
                data-testid="admin-ai-queue-triage-threshold-explainer"
              >
                {t("admin.ai.queues.triageThresholdExplainer", {
                  auto: form.triage_auto_threshold || "100",
                  suggestLow: form.triage_suggest_threshold || "0",
                  suggestHigh: Math.max(
                    0,
                    (Number(form.triage_auto_threshold) || 100) - 1,
                  ),
                })}
              </p>

              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  data-testid="admin-ai-queue-form-triage_delay_reply"
                  checked={form.triage_delay_reply}
                  onChange={(e) =>
                    setField("triage_delay_reply", e.target.checked)
                  }
                  className="rounded border-hairline"
                />
                {t("admin.ai.queues.triageDelayReply")}
                <HelpPopover
                  title={t("admin.ai.queues.triageDelayReply")}
                  testId="admin-ai-queue-help-triage_delay_reply"
                >
                  {t("admin.help.aiQueue.triageDelayReply")}
                </HelpPopover>
              </label>

              <label className="flex items-center gap-2 text-sm text-ink">
                <input
                  type="checkbox"
                  data-testid="admin-ai-queue-form-triage_customer_fix_enabled"
                  checked={form.triage_customer_fix_enabled}
                  onChange={(e) =>
                    setField("triage_customer_fix_enabled", e.target.checked)
                  }
                  className="rounded border-hairline"
                />
                {t("admin.ai.queues.triageCustomerFix")}
                <HelpPopover
                  title={t("admin.ai.queues.triageCustomerFix")}
                  testId="admin-ai-queue-help-triage_customer_fix_enabled"
                >
                  {t("admin.help.aiQueue.triageCustomerFix")}
                </HelpPopover>
              </label>

              <div className="grid gap-4 sm:grid-cols-3">
                <label className="block text-sm">
                  <FieldLabel
                    text={t("admin.ai.queues.triageCustomerFixThreshold")}
                    help={t("admin.help.aiQueue.triageCustomerFixThreshold")}
                    testId="admin-ai-queue-help-triage_customer_fix_auto_threshold"
                  />
                  <input
                    type="number"
                    min={0}
                    max={100}
                    data-testid="admin-ai-queue-form-triage_customer_fix_auto_threshold"
                    value={form.triage_customer_fix_auto_threshold}
                    onChange={(e) =>
                      setField(
                        "triage_customer_fix_auto_threshold",
                        e.target.value,
                      )
                    }
                    className="mt-1 w-full rounded-md border border-hairline bg-surface px-2 py-1.5 text-sm text-ink"
                  />
                </label>
              </div>
            </fieldset>
          </div>
        )}

        {tab === "safety" && (
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-pii_masking"
                checked={form.pii_masking}
                onChange={(e) => setField("pii_masking", e.target.checked)}
                className="rounded border-hairline"
              />
              {t("admin.ai.queues.piiMasking")}
              <HelpPopover
                title={t("admin.ai.queues.piiMasking")}
                testId="admin-ai-queue-help-pii_masking"
              >
                {t("admin.help.aiQueue.piiMasking")}
              </HelpPopover>
            </label>
            <label className="flex items-center gap-2 text-sm text-ink sm:col-span-2">
              <input
                type="checkbox"
                data-testid="admin-ai-queue-form-pii_ner_enabled"
                checked={form.pii_ner_enabled}
                disabled={!form.pii_masking}
                onChange={(e) => setField("pii_ner_enabled", e.target.checked)}
                className="rounded border-hairline"
              />
              {t("admin.ai.queues.piiNer")}
              <HelpPopover
                title={t("admin.ai.queues.piiNer")}
                testId="admin-ai-queue-help-pii_ner_enabled"
              >
                {t("admin.help.aiQueue.piiNer")}
              </HelpPopover>
            </label>
            <label className="block text-sm sm:col-span-2">
              <FieldLabel
                text={t("admin.ai.queues.section.identity")}
                help={t("admin.help.aiQueue.identityMode")}
                defaultHint={t(
                  "admin.ai.queues.identityMode.ticket_customer_id",
                )}
                testId="admin-ai-queue-help-identity_mode"
              />
              <PickerField
                testId="admin-ai-queue-form-identity_mode"
                value={form.identity_mode}
                items={IDENTITY_MODES.map((m) => ({
                  value: m,
                  label: t(`admin.ai.queues.identityMode.${m}`),
                }))}
                placeholder={t("admin.form.selectPlaceholder")}
                onSelect={(v) => setField("identity_mode", v)}
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <FieldLabel
                text={t("admin.ai.queues.allowedStateTypes")}
                help={t("admin.help.aiQueue.allowedStateTypes")}
                defaultHint="open"
                testId="admin-ai-queue-help-allowed_state_types"
              />
              <input
                data-testid="admin-ai-queue-form-allowed_state_types"
                value={form.allowed_state_types}
                onChange={(e) =>
                  setField("allowed_state_types", e.target.value)
                }
                placeholder="open"
                className={inputClass}
              />
            </label>
            {form.identity_mode === "clarify_schema" && (
              <label className="block text-sm sm:col-span-2">
                <FieldLabel
                  text={t("admin.ai.queues.clarifySchemaJson")}
                  help={t("admin.help.aiQueue.clarifySchemaJson")}
                  testId="admin-ai-queue-help-clarify_schema_json"
                />
                <textarea
                  data-testid="admin-ai-queue-form-clarify_schema_json"
                  value={form.clarify_schema_json}
                  onChange={(e) =>
                    setField("clarify_schema_json", e.target.value)
                  }
                  placeholder={t(
                    "admin.ai.queues.clarifySchemaJsonPlaceholder",
                  )}
                  rows={3}
                  spellCheck={false}
                  className={cn(inputClass, "font-mono text-xs")}
                />
                {jsonErrors.clarify && (
                  <p
                    className="text-xs text-escalation"
                    data-testid="admin-ai-queue-form-clarify_schema_json-error"
                  >
                    {jsonErrors.clarify}
                  </p>
                )}
              </label>
            )}
          </div>
        )}
      </div>

      {confirmDialog}
    </div>
  );
}

export function AiQueuePolicyNewPage() {
  return <AiQueuePolicyEditor />;
}

export function AiQueuePolicyEditPage() {
  const { policyId } = useParams({ from: "/admin/ai/queues/$policyId" });
  return <AiQueuePolicyEditor policyId={Number(policyId)} />;
}
